import { API_URL, authenticatedFetch } from "../config/api";
import { ItemListQuery, LIST_DEFAULT_SIZE, LIST_MAX_SIZE, Paginated } from "../types/pagination";
import { getCachedItemList, itemListCacheKey, setCachedItemList } from "../utils/itemListCache";
import { appendFlag, appendListFilter, appendPageParams, appendTextFilter } from "./listParams";

/**
 * Safe JSON parsing with empty response handling
 */
const safeJsonParse = async (response: Response): Promise<any> => {
  const text = await response.text();
  if (!text || text.trim() === '') {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch (error) {
    console.warn('Failed to parse JSON response:', text);
    throw new Error('Invalid JSON response from server');
  }
};

/**
 * Fetch one page of action items for a project, with server-side sorting and filtering.
 * @param {string} _token - deprecated, token is handled automatically
 * @param {string} project_id - the project the items belong to
 * @param {ItemListQuery} query - paging, sort and filter options
 * @returns {Promise<ProjectActionItemsInterface>} the requested page
 * @throws Will throw an error if the request fails.
 */
/**
 * Fetch one page of action items for a project. Pass `useCache` to reuse a recent identical response.
 * @param {string} _token - deprecated, token is handled automatically
 * @param {string} project_id - the project the items belong to
 * @param {ItemListQuery} query - paging, sort and filter options
 * @param {{useCache?: boolean}} options - set `useCache` to read from and write to the in-memory list cache
 * @returns {Promise<Paginated<ProjectActionItem>>} the requested page
 */
export const getProjectActionItems = async (_token: string, project_id: string, query: ItemListQuery = {}, options: { useCache?: boolean } = {}): Promise<Paginated<ProjectActionItem>> => {
  const cacheKey = itemListCacheKey('actionItems', project_id, query);
  if (options.useCache) {
    const cached = getCachedItemList<Paginated<ProjectActionItem>>(cacheKey);
    if (cached) return cached;
  }
  const data = await fetchProjectActionItemsPage(_token, project_id, query);
  if (options.useCache) setCachedItemList(cacheKey, data);
  return data;
};

const fetchProjectActionItemsPage = async (_token: string, project_id: string, query: ItemListQuery): Promise<Paginated<ProjectActionItem>> => {
  try {
    const params = new URLSearchParams();
    appendPageParams(params, query, LIST_DEFAULT_SIZE);
    appendFlag(params, 'archived', query.archived);
    appendTextFilter(params, 'name', query.name);
    appendTextFilter(params, 'description', query.description);
    appendListFilter(params, 'phase_id', query.phaseIds);
    appendListFilter(params, 'owner_id', query.ownerIds);
    appendListFilter(params, 'assigned_to', query.assignedToIds);
    appendFlag(params, 'owner_is_null', query.ownerIsNull);
    appendFlag(params, 'assigned_to_is_null', query.assignedToIsNull);
    appendListFilter(params, 'status_id', query.statusIds);
    appendListFilter(params, 'priority_id', query.priorityIds);
    appendListFilter(params, 'status', query.statusValues);
    appendListFilter(params, 'priority', query.priorityValues);
    appendTextFilter(params, 'created_at_from', query.createdAtFrom);
    appendTextFilter(params, 'created_at_to', query.createdAtTo);
    appendTextFilter(params, 'start_date_from', query.startDateFrom);
    appendTextFilter(params, 'start_date_to', query.startDateTo);
    appendTextFilter(params, 'due_date_from', query.dueDateFrom);
    appendTextFilter(params, 'due_date_to', query.dueDateTo);
    appendTextFilter(params, 'q', query.search);
    appendTextFilter(params, 'risk', query.risk);
    appendListFilter(params, 'room_id', query.roomIds);
    const r = await authenticatedFetch(`${API_URL}/action_items/${project_id}/items?${params}`);

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: Paginated<ProjectActionItem> = await r.json();
    return data;
  } catch(e: Error | any) {
    throw new Error(e.message || 'Failed to fetch project action items');
  }
};

/**
 * Fetch every action item for a project by walking all pages at the maximum page size.
 * Only for consumers that need the full set, such as risk filtering.
 * @param {string} token - deprecated, token is handled automatically
 * @param {string} project_id - the project the items belong to
 * @param {Omit<ItemListQuery, 'page' | 'size'>} query - filters and sort applied to every page
 * @returns {Promise<ProjectActionItem[]>} all matching action items
 */
export const getAllProjectActionItems = async (token: string, project_id: string, query: Omit<ItemListQuery, 'page' | 'size'> = {}): Promise<ProjectActionItem[]> => {
  const all: ProjectActionItem[] = [];
  for (let page = 1; ; page++) {
    const data = await getProjectActionItems(token, project_id, { ...query, page, size: LIST_MAX_SIZE });
    all.push(...(data.results ?? []));
    if (!data.results?.length || all.length >= data.total) break;
  }
  return all;
};

export const getProjectActionItemsById = async (_token: string, project_id: string, item_id: string): Promise<ProjectActionItem> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/action_items/${project_id}/items/${item_id}`);
    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
    const data:any = await r.json();
    return data;
  } catch(e: Error | any) {
    throw new Error(e.message || 'Failed to fetch project action item');
  }
}

export const createProjectActionItems = async (_token: string, project_id: string, data: ProjectActionItem): Promise<ProjectActionItem> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/action_items/${project_id}/items`,{
      method: 'POST',
      body: JSON.stringify(data)
    });
    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
    const res:any = await r.json();
    return res;
  } catch(e: Error | any) {
    throw new Error(e.message || 'Failed to create project action item');
  }
}

export const updateProjectActionItems = async (_token: string, project_id: string, item_id: string, data: ProjectActionItem): Promise<ProjectActionItem> => {
  try {
    const res = await authenticatedFetch(`${API_URL}/action_items/${project_id}/items/${item_id}`,{
      method: 'PUT',
      body: JSON.stringify(data)
    });

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    }

    const responseData = await safeJsonParse(res);
    
    // If server returns empty response, return the updated data
    if (!responseData) {
      return {
        ...data,
        id: item_id
      } as ProjectActionItem;
    }

    return responseData;
  } catch(e: Error | any) {
    if (e.message?.includes('JSON')) {
      throw new Error('Server returned invalid response. The action item may have been updated successfully.');
    }
    throw new Error(e.message || 'Failed to update project action item');
  }
};

export const deleteProjectActionItems = async (_token: string, project_id: string, item_id: string): Promise<void> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/action_items/${project_id}/items/${item_id}`,{
      method: 'DELETE'
    });
    
    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    return;
  } catch(e: Error | any) {
    throw new Error(e.message || 'Failed to delete project action item');
  }
};

export const createProjectActionItemDocument = async (
  _token: string,
  project_id: string,
  item_id: string,
  file: File,
  description?: string
): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }

    const r = await authenticatedFetch(`${API_URL}/action_items/${project_id}/items/${item_id}/document`, {
      method: 'POST',
      body: formData
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data:any = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to create project action item document');
  }
};

export const getProjectActionItemDocument = async (
  _token: string,
  project_id: string,
  item_id: string,
  file_name?: string
): Promise<any> => {
  try {
    const url = file_name 
      ? `${API_URL}/action_items/${project_id}/items/${item_id}/document/${file_name}`
      : `${API_URL}/action_items/${project_id}/items/${item_id}/document`;

    const r = await authenticatedFetch(url);

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    // Check if response is JSON (metadata) or file content
    const contentType = r.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await r.json();
    } else {
      // Return the response for file download
      return r;
    }
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to get project action item document');
  }
};

export const updateProjectActionItemDocument = async (
  _token: string,
  project_id: string,
  item_id: string,
  file: File,
  description?: string
): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }

    const r = await authenticatedFetch(`${API_URL}/action_items/${project_id}/items/${item_id}/document`, {
      method: 'PUT',
      body: formData
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data:any = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to update project action item document');
  }
};

export const deleteProjectActionItemDocument = async (
  _token: string,
  project_id: string,
  item_id: string
): Promise<any> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/action_items/${project_id}/items/${item_id}/document`, {
      method: 'DELETE'
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    // DELETE may return JSON response or empty
    const contentType = r.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await r.json();
    }
    
    return null;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to delete project action item document');
  }
};
